import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert } from '@piggytechng/component-library';
import { httpClient } from '../api/http';
import { authPayload } from '../helpers/utils/auth-payload';
import { AccountState } from '../types';
import useRolePermissions from './useRolePermissions';

// Deactivate / reactivate a customer account (core: admin/user/account-state, deactivate, reactivate).
const useAccountState = (userId: string) => {
  const { restrictActionByRole, isSuperAdmin } = useRolePermissions({});
  const queryClient = useQueryClient();
  const [isRunningAccountStateAction, setIsRunningAccountStateAction] = useState<boolean>(false);

  const {
    data: accountState,
    isLoading: isLoadingAccountState,
    refetch: refetchAccountState,
  } = useQuery<AccountState>({
    queryKey: ['account-state' + userId],
    enabled: !!userId,
    retry: false,
    queryFn: () =>
      httpClient.get(`admin/user/account-state/${userId}`, { hideError: true }).then((res) => res.data?.data),
  });

  const refreshCustomer = () => {
    refetchAccountState();
    // The profile payload carries accountState too, so the header badge follows the change.
    queryClient.invalidateQueries({ queryKey: [`single-customer-${userId}`] });
  };

  const deactivateAccount = async (reason: string, password: string, callback?: () => void) => {
    if (restrictActionByRole('flagAndActions', 'deactivateAccount')) return;
    if (!isSuperAdmin) {
      Alert.warn('You do not have access to perform this action.');
      return;
    }
    try {
      setIsRunningAccountStateAction(true);
      const response = await httpClient.post<any>(`admin/user/deactivate/${userId}`, {
        reason,
        ...authPayload(password),
      });
      if (response.data.status) {
        Alert.success(response.data.message || 'Account deactivated successfully');
        callback?.();
        refreshCustomer();
      }
    } catch (err: any) {
      console.log(err?.response?.data?.message || 'An error occured trying to deactivate the account');
    } finally {
      setIsRunningAccountStateAction(false);
    }
  };

  const reactivateAccount = async (note: string, password: string, callback?: () => void) => {
    if (restrictActionByRole('flagAndActions', 'reactivateAccount')) return;
    if (!isSuperAdmin) {
      Alert.warn('You do not have access to perform this action.');
      return;
    }
    try {
      setIsRunningAccountStateAction(true);
      const response = await httpClient.post<any>(`admin/user/reactivate/${userId}`, {
        note,
        ...authPayload(password),
      });
      if (response.data.status) {
        Alert.success(response.data.message || 'Account reactivated successfully');
        callback?.();
        refreshCustomer();
      }
    } catch (err: any) {
      console.log(err?.response?.data?.message || 'An error occured trying to reactivate the account');
    } finally {
      setIsRunningAccountStateAction(false);
    }
  };

  return {
    accountState,
    isLoadingAccountState,
    refetchAccountState,
    deactivateAccount,
    reactivateAccount,
    isRunningAccountStateAction,
  };
};

export default useAccountState;
